import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'components/ui/form/typeahead.scss';

// Typeahead custom (from scratch) — remplace react-theme-typeahead.
// Combobox single-select : filtre live, navigation clavier (↑/↓/Enter/Esc),
// bouton clear et option "add new" pour les valeurs libres (allowNew).
const normalize = (opt) => {
  if (typeof opt === 'string') {
    return { label: opt, raw: opt };
  }
  return { label: opt && opt.label != null ? String(opt.label) : '', raw: opt };
};

const Typeahead = ({
  id,
  disabled = false,
  options = [],
  selected = [],
  onChange,
  allowNew = false,
  clearButton = false,
  placeholder,
  newOptionLabel = 'Add',
  className,
}) => {
  const normOptions = useMemo(() => options.map(normalize), [options]);
  const selectedLabel = selected && selected.length ? normalize(selected[0]).label : '';

  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return normOptions;
    }
    return normOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, normOptions]);

  const trimmedQuery = query.trim();
  const showAddNew =
    allowNew && !!trimmedQuery && !normOptions.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase());
  const items = showAddNew ? [...filtered, { label: trimmedQuery, raw: { label: trimmedQuery }, isNew: true }] : filtered;

  const select = (item) => {
    setQuery(item.label);
    setOpen(false);
    onChange && onChange([item.raw]);
  };

  const clear = () => {
    setQuery('');
    setOpen(false);
    onChange && onChange([]);
  };

  const onKeyDown = (e) => {
    if (disabled) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && items[highlight]) {
        e.preventDefault();
        select(items[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={classNames('ui-typeahead', className)}>
      <div className="ui-typeahead__control">
        <input
          id={id}
          type="text"
          className="form-control ui-typeahead__input"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {clearButton && !!query && !disabled && (
          <button type="button" className="ui-typeahead__clear" onClick={clear} aria-label="Clear" tabIndex={-1}>
            ×
          </button>
        )}
      </div>
      {open && items.length > 0 && (
        <ul className="ui-typeahead__menu" role="listbox">
          {items.map((item, i) => (
            <li
              key={`${item.label}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={classNames('ui-typeahead__option', {
                'ui-typeahead__option--active': i === highlight,
                'ui-typeahead__option--new': item.isNew,
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                select(item);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              {item.isNew ? `${newOptionLabel} : ${item.label}` : item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

Typeahead.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  selected: PropTypes.array,
  onChange: PropTypes.func,
  allowNew: PropTypes.bool,
  clearButton: PropTypes.bool,
  placeholder: PropTypes.string,
  newOptionLabel: PropTypes.string,
  className: PropTypes.string,
};

export default Typeahead;
